// @ts-ignore
import environment from '../environment.json';

import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import AuthService from '../services/AuthService';
import AsyncAuthService from '../services/AsyncAuthService';
import EnvironmentService from '../services/EnvironmentService';
import ApiResponseV2 from '../core/ApiResponseV2';
import { ApiResultDTO } from '../core/ApiResultDTO';

export interface HttpClientV2Config {
  /**
   * Used to override axios default config
   */
  axiosConfig?: AxiosRequestConfig;
}

/**
 * An HTTP client wrapper around Axios.
 *
 * @param baseURL - The base URL for all API requests.
 * @param requestTimeout - The timeout in milliseconds for all requests, 5000 ms by default
 */
export class HttpClientV2 {
  private client: AxiosInstance;

  /**
   * Constructs a new `HttpClientV2`.
   *
   * @param {string} baseURL - The base URL for all API requests.
   * @param {number} [requestTimeout=5000] - The timeout in milliseconds for all requests.
   */
  constructor(baseURL: string, requestTimeout?: number) {
    this.client = axios.create({
      baseURL: baseURL,
      timeout: requestTimeout || 5000,
    });

    this.generateConfig().then((config) => {
      if (!!config.headers?.Authorization) {
        axios.defaults.headers.common['Authorization'] = config.headers!.Authorization;
      }
    });
  }

  /**
   * Adds an `Authorization` header to the given `AxiosRequestConfig` object.
   *
   * @private
   * @param {AxiosRequestConfig} config - The configuration object to add the header to.
   * @param {string} token - The authorization token to include in the header.
   */
  private addBaseConfig(config: AxiosRequestConfig, token: any) {
    if (token) {
      if (!config.headers) {
        config.headers = {};
      }
      config.headers['Authorization'] = `Bearer ${token}`;
    }
  }

  /**
   * Generates a new `AxiosRequestConfig` object with the appropriate headers.
   *
   * @private
   * @param {AxiosRequestConfig} [extraConfig] - Additional configuration options to include.
   * @returns {Promise<AxiosRequestConfig>} A promise that resolves to the generated configuration object.
   */
  private async generateConfig(extraConfig?: AxiosRequestConfig): Promise<AxiosRequestConfig> {
    var config: AxiosRequestConfig = extraConfig || {};

    if (EnvironmentService.usesAsyncStorageStrategy()) {
      this.addBaseConfig(config, await AsyncAuthService.getToken());
    } else {
      this.addBaseConfig(config, AuthService.getToken());
    }

    return config;
  }

  /**
   * Handles an error that occurred during an Axios request.
   *
   * @private
   * @template T
   * @param {Object} error - The Axios error object.
   * @param {AxiosResponse<ApiResultDTO<T>> | undefined} error.response - The Axios response object, if available.
   * @param {XMLHttpRequest | undefined} error.request - The XMLHttpRequest or http.ClientRequest object, if available.
   * @returns {ApiResponseV2<T>} An ApiResponseV2 object representing the error.
   */
  private handleAxiosRequestError<T>(error: {
    response: AxiosResponse<ApiResultDTO<T>> | undefined;
    request: XMLHttpRequest | undefined;
  }): ApiResponseV2<T> {
    if (error.response) {
      // The request was made and the server responded with a status code that falls out of the range of 2xx
      return ApiResponseV2.fromAxiosResponse(error.response);
    }

    if (error.request) {
      // The request was made but no response was received
      // `error.request` is an instance of XMLHttpRequest in the browser and an instance of http.ClientRequest in node.js
      return ApiResponseV2.noResponseError();
    }

    // Something happened in setting up the request that triggered an Error
    return ApiResponseV2.requestError();
  }

  /**
   * Sends a GET request to the API.
   *
   * @async
   * @template T
   * @param {string} url - The URL for the request.
   * @param {HttpClientV2Config} [config] - Additional configuration options for the request.
   * @returns {Promise<ApiResponseV2<T>>} A promise that resolves to an ApiResponseV2 object.
   */
  async get<T>(url: string, config?: HttpClientV2Config): Promise<ApiResponseV2<T>> {
    return this.client
      .get(url, await this.generateConfig(config?.axiosConfig))
      .then((response: AxiosResponse<ApiResultDTO<T>>) => ApiResponseV2.fromAxiosResponse(response))
      .catch((error) => this.handleAxiosRequestError(error));
  }

  /**
   * Performs an HTTP POST request with the given payload.
   *
   * @template T The expected response data type.
   * @template R The type of the payload data.
   * @param {string} url The URL to send the request to.
   * @param {R} payload The payload to send with the request.
   * @param {HttpClientV2Config} [config] Optional configuration overrides.
   * @returns {Promise<ApiResponseV2<R>>} A Promise that resolves with the response data.
   */
  async post<T, R>(url: string, payload: T, config?: HttpClientV2Config): Promise<ApiResponseV2<R>> {
    return this.client
      .post(url, payload, await this.generateConfig(config?.axiosConfig))
      .then((response: AxiosResponse<ApiResultDTO<R>>) => ApiResponseV2.fromAxiosResponse(response))
      .catch((error) => this.handleAxiosRequestError(error));
  }

  /**
   * Performs an HTTP PUT request with the given payload.
   *
   * @template T The expected response data type.
   * @template R The type of the payload data.
   * @param {string} url The URL to send the request to.
   * @param {R} payload The payload to send with the request.
   * @param {HttpClientV2Config} [config] Optional configuration overrides.
   * @returns {Promise<ApiResponseV2<R>>} A Promise that resolves with the response data.
   */
  async put<T, R>(url: string, payload: T, config?: HttpClientV2Config): Promise<ApiResponseV2<R>> {
    return this.client
      .put(url, payload, await this.generateConfig(config?.axiosConfig))
      .then((response: AxiosResponse<ApiResultDTO<R>>) => ApiResponseV2.fromAxiosResponse(response))
      .catch((error) => this.handleAxiosRequestError(error));
  }

  /**
   * Performs an HTTP PUT request with form data.
   *
   * @template R The expected response data type.
   * @param {string} url The URL to send the request to.
   * @param {R} body The form data payload to send with the request.
   * @param {HttpClientV2Config} [config] Optional configuration overrides.
   * @returns {Promise<ApiResponseV2<R>>} A Promise that resolves with the response data.
   */
  async putForm<R>(url: string, body: FormData, config?: HttpClientV2Config): Promise<ApiResponseV2<R>> {
    const extraConfig = {
      ...config?.axiosConfig,
      headers: {
        ...config?.axiosConfig?.headers,
        'Content-Type': 'multipart/form-data',
      },
    };

    return this.client
      .put(url, body, await this.generateConfig(extraConfig))
      .then((response: AxiosResponse<ApiResultDTO<R>>) => ApiResponseV2.fromAxiosResponse(response))
      .catch((error) => this.handleAxiosRequestError(error));
  }

  /**
   * Performs an HTTP DELETE request.
   *
   * @template T The expected response data type.
   * @param {string} url The URL to send the request to.
   * @param {HttpClientV2Config} [config] Optional configuration overrides.
   * @returns {Promise<ApiResponseV2<T>>} A Promise that resolves with the response data.
   */
  async delete<T>(url: string, config?: HttpClientV2Config): Promise<ApiResponseV2<T>> {
    return this.client
      .delete(url, await this.generateConfig(config?.axiosConfig))
      .then((response: AxiosResponse<ApiResultDTO<T>>) => ApiResponseV2.fromAxiosResponse(response))
      .catch((error) => this.handleAxiosRequestError(error));
  }

  /**
   * Sends a GET FILE request to the API.
   *
   * @async
   * @template T The expected response data type.
   * @param {string} url - The URL for the request.
   * @param {HttpClientV2Config} [config] - Additional configuration options for the request.
   * @returns {Promise<ApiResponseV2<T>>} A promise that resolves to an ApiResponseV2 object.
   */
  async getFile<T>(url: string, config?: HttpClientV2Config): Promise<ApiResponseV2<T>> {
    const customConfig = await this.generateConfig(config?.axiosConfig);
    customConfig.responseType = 'blob';
    return this.client
      .get(url, customConfig)
      .then((response: AxiosResponse<ApiResultDTO<T>>) => ApiResponseV2.fromAxiosResponse(response))
      .catch((error) => this.handleAxiosRequestError(error));
  }

  /**
   * Performs an HTTP POST request with the given payload.
   *
   * @template T The expected response data type.
   * @template R The type of the payload data.
   * @param {string} url The URL to send the request to.
   * @param {R} payload The payload to send with the request.
   * @param {HttpClientV2Config} [config] Optional configuration overrides.
   * @returns {Promise<ApiResponseV2<R>>} A Promise that resolves with the response data.
   */
  async postFile<T, R>(url: string, payload: R, config?: HttpClientV2Config): Promise<ApiResponseV2<T>> {
    const customConfig = await this.generateConfig();
    customConfig.responseType = 'blob';
    return this.client
      .post(url, payload, customConfig)
      .then((response: AxiosResponse<ApiResultDTO<T>>) => ApiResponseV2.fromAxiosResponse(response))
      .catch((error) => this.handleAxiosRequestError(error));
  }

  /**
   * Performs an HTTP PATCH request with the given payload.
   *
   * @template T The expected response data type.
   * @template R The type of the payload data.
   * @param {string} url The URL to send the request to.
   * @param {R} payload The payload to send with the request.
   * @param {HttpClientV2Config} [config] Optional configuration overrides.
   * @returns {Promise<ApiResponseV2<R>>} A Promise that resolves with the response data.
   */
  async patchWithPayload<T, R>(url: string, payload: T, config?: HttpClientV2Config): Promise<ApiResponseV2<R>> {
    return this.client
      .patch(url, payload, await this.generateConfig(config?.axiosConfig))
      .then((response: AxiosResponse<ApiResultDTO<R>>) => ApiResponseV2.fromAxiosResponse(response))
      .catch((error) => this.handleAxiosRequestError(error));
  }

  /**
   * Performs an HTTP PATCH request without payload.
   *
   * @template T The expected response data type.
   * @param {string} url The URL to send the request to.
   * @returns {Promise<ApiResponseV2<T>>} A Promise that resolves with the response data.
   */
  async patchWithoutPayload<T>(url: string, config?: HttpClientV2Config): Promise<ApiResponseV2<T>> {
    return this.client
      .patch(url, {}, await this.generateConfig(config?.axiosConfig))
      .then((response: AxiosResponse<ApiResultDTO<T>>) => ApiResponseV2.fromAxiosResponse(response))
      .catch((error) => this.handleAxiosRequestError(error));
  }
}

export default new HttpClientV2(environment.baseURL, environment.requestTimeout);
